import { isGrantCurrent, type AppDefinition, type AppGrant, type LicenseRequest } from "@acruxsoft/platform-shared";

/**
 * What a given user can do with a given app right now.
 *
 * - `comingSoon` — the app isn't deployed yet. A property of the app, not of
 *   the user, so it wins over everything else.
 * - `unverified` — email not confirmed. mintRedirectToken refuses to issue SSO
 *   tokens until it is, so no app can be opened in this state.
 * - `open`      — a free app, or a licensed one the user holds a current
 *   license (or entitlement) for.
 * - `requested` — licensed, not held, and the user already asked for it.
 * - `expired`   — licensed, and the user's license ran out.
 * - `locked`    — licensed and never held (or revoked): offer to request it.
 *
 * Licensed apps are never hidden: they show locked, with a request button.
 */
export type AppAccess = "comingSoon" | "unverified" | "open" | "requested" | "expired" | "locked";

export interface AppAccessInput {
  app: AppDefinition;
  emailVerified: boolean;
  entitlements: readonly string[];
  /** The user's license for this app, if any, read from /appGrants. */
  grant: AppGrant | null;
  /** The user's request for this app, if any, read from /licenseRequests. */
  request: LicenseRequest | null;
  now: number;
}

export function appAccess(input: AppAccessInput): AppAccess {
  const { app, grant, request, now } = input;
  if (!app.available) return "comingSoon";
  if (!input.emailVerified) return "unverified";
  if (app.access === "free") return "open";
  // The token's entitlements lag a just-granted license by up to an hour; the
  // grant doc doesn't. Opening refreshes the token first, so trusting the doc
  // here sends the user straight in instead of showing a stale lock.
  if (input.entitlements.includes(app.entitlement)) return "open";
  if (grant && isGrantCurrent(grant, now)) return "open";
  if (request?.status === "pending") return "requested";
  if (grant && grant.status !== "revoked") return "expired";
  return "locked";
}

/** The end date of a current license, for "licensed until …" copy. Null otherwise. */
export function licenseEndsAt(grant: AppGrant | null, now: number): number | null {
  return grant && isGrantCurrent(grant, now) ? grant.expiresAt : null;
}
